import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Label, Input, Textarea, Select } from "@/components/Field";
import CTA from "@/components/CTA";

const TIME_SLOTS = ["Morning (8–11am)", "Midday (11am–1pm)", "Afternoon (1–4pm)", "After hours"];
const ROLE_OPTIONS = ["Administrator / Principal", "Office / Dismissal Staff", "Teacher", "District / IT", "Other"];

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  school_name: "",
  location: "",
  role: "",
  trial_days: 60,
  preferred_date: "",
  preferred_time: "",
  message: "",
};

export default function LeadForm({ variant = "trial", product = "campuspath", dark = false }) {
  const isTrial = variant === "trial";
  const brand = product === "testguard" ? "TestGuard" : "CampusPath";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    if (done && isTrial) {
      api.get("/downloads", { params: { product } })
        .then((r) => setDownloads(r.data || []))
        .catch(() => setDownloads([]));
    }
  }, [done, isTrial, product]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        lead_type: variant,
        product,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone || null,
        school_name: form.school_name,
        location: form.location || null,
        role: form.role || null,
        message: form.message || null,
        ...(isTrial
          ? { trial_days: Number(form.trial_days), origin: window.location.origin }
          : { preferred_date: form.preferred_date || null, preferred_time: form.preferred_time || null }),
      };
      await api.post("/leads", payload);
      setDone(true);
      toast.success(isTrial ? "Trial request received!" : "Demo request received!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const heading = dark ? "text-white" : "text-ink";
  const muted = dark ? "text-white/60" : "text-ink/60";

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="rounded-2xl border border-leaf/40 bg-leaf/10 p-10 text-center"
        data-testid="lead-success"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-leaf text-ink">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <h3 className={`mt-6 font-display text-2xl font-800 ${heading}`}>You're all set, {form.first_name || "there"}.</h3>
        <p className={`mx-auto mt-3 max-w-sm ${muted}`}>
          {isTrial
            ? `We're preparing your ${form.trial_days}-day ${brand} trial${downloads.length ? ", your downloads are ready below and on their way to " : ". Our team will email "}${form.email}${downloads.length ? "." : " with access shortly."}`
            : `Thanks! We'll reach out at ${form.email} to lock in your demo time.`}
        </p>
        {isTrial && downloads.length > 0 && (
          <div className="mx-auto mt-6 max-w-sm space-y-2 text-left" data-testid="trial-downloads">
            <div className={`text-xs font-600 uppercase tracking-widest ${dark ? "text-white/40" : "text-ink/40"}`}>Your downloads</div>
            {downloads.map((d) => (
              <a
                key={d.id}
                href={d.download_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`download-${d.id}`}
                className={`flex items-center justify-between rounded-xl border px-4 py-3 transition-colors duration-200 hover:border-leaf ${
                  dark ? "border-white/15 bg-white/5 text-white" : "border-ink/15 bg-white text-ink"
                }`}
              >
                <span className="font-600">
                  {d.label}
                  {d.size_label ? <span className={`ml-1 ${dark ? "text-white/40" : "text-ink/40"}`}>· {d.size_label}</span> : null}
                </span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-leaf"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </a>
            ))}
          </div>
        )}
        <div className="mt-8 flex justify-center gap-3">
          <CTA to="/" variant={dark ? "leafDark" : "ink"} dataTestId="success-home-btn">Back to home</CTA>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-7" data-testid="lead-form">
      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label htmlFor="first_name" dark={dark} required>First name</Label>
          <Input id="first_name" dark={dark} dataTestId="input-first-name" value={form.first_name} onChange={set("first_name")} required placeholder="Jane" />
        </div>
        <div>
          <Label htmlFor="last_name" dark={dark} required>Last name</Label>
          <Input id="last_name" dark={dark} dataTestId="input-last-name" value={form.last_name} onChange={set("last_name")} required placeholder="Doe" />
        </div>
      </div>

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label htmlFor="email" dark={dark} required>Work / school email</Label>
          <Input id="email" type="email" dark={dark} dataTestId="input-email" value={form.email} onChange={set("email")} required placeholder="jane@school.org" />
        </div>
        <div>
          <Label htmlFor="phone" dark={dark}>Phone</Label>
          <Input id="phone" dark={dark} dataTestId="input-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
        </div>
      </div>

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label htmlFor="school_name" dark={dark} required>School / district</Label>
          <Input id="school_name" dark={dark} dataTestId="input-school" value={form.school_name} onChange={set("school_name")} required placeholder="Lincoln Elementary" />
        </div>
        <div>
          <Label htmlFor="location" dark={dark}>Location (city & state)</Label>
          <Input id="location" dark={dark} dataTestId="input-location" value={form.location} onChange={set("location")} placeholder="Austin, TX" />
        </div>
      </div>

      <div>
        <Label htmlFor="role" dark={dark}>Your role</Label>
        <Select id="role" dark={dark} dataTestId="select-role" value={form.role} onChange={set("role")}>
          <option value="">Select a role…</option>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
      </div>

      {isTrial ? (
        <div data-testid="trial-length-field">
          <Label htmlFor="trial_days" dark={dark}>Trial length, <span className="font-800 text-leaf">{form.trial_days} days</span></Label>
          <input
            id="trial_days"
            data-testid="input-trial-days"
            type="range"
            min={45}
            max={90}
            step={5}
            value={form.trial_days}
            onChange={set("trial_days")}
            className="mt-3 w-full accent-leaf"
          />
          <div className={`mt-2 flex justify-between text-xs ${dark ? "text-white/40" : "text-ink/40"}`}>
            <span>45 days</span><span>90 days</span>
          </div>
          <div className="mt-4 flex gap-2">
            {[45, 60, 75, 90].map((d) => (
              <button
                type="button"
                key={d}
                onClick={() => setForm((f) => ({ ...f, trial_days: d }))}
                data-testid={`trial-preset-${d}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-600 transition-colors duration-200 ${
                  Number(form.trial_days) === d
                    ? "border-leaf bg-leaf text-ink"
                    : dark
                      ? "border-white/25 text-white/70 hover:border-white"
                      : "border-ink/20 text-ink/60 hover:border-ink"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid gap-7 sm:grid-cols-2">
          <div>
            <Label htmlFor="preferred_date" dark={dark}>Preferred date</Label>
            <Input id="preferred_date" type="date" dark={dark} dataTestId="input-date" value={form.preferred_date} onChange={set("preferred_date")} />
          </div>
          <div>
            <Label htmlFor="preferred_time" dark={dark}>Preferred time</Label>
            <Select id="preferred_time" dark={dark} dataTestId="select-time" value={form.preferred_time} onChange={set("preferred_time")}>
              <option value="">Any time…</option>
              {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="message" dark={dark}>Notes / questions</Label>
        <Textarea id="message" dark={dark} dataTestId="input-message" value={form.message} onChange={set("message")} placeholder="Tell us about your school…" />
      </div>

      <div className="flex items-center gap-4 pt-2">
        <button
          type="submit"
          disabled={loading}
          data-testid="lead-submit-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-leaf px-8 py-4 text-sm font-700 text-ink transition-[background-color,transform] duration-300 hover:bg-ink hover:text-white active:scale-[0.97] disabled:opacity-60"
        >
          {loading ? "Sending…" : isTrial ? "Start my free trial" : "Request my demo"}
          {!loading && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="transition-transform duration-300 group-hover:translate-x-1"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
        </button>
        <span className={`text-xs ${dark ? "text-white/40" : "text-ink/40"}`}>No credit card required.</span>
      </div>
    </form>
  );
}
